'use client'

import { useEffect, useRef, useState, useCallback } from 'react'

interface ScrollVideoProps {
  src: string
  poster?: string
  frameCount?: number
  maxFrameWidth?: number
}

export function ScrollVideo({ src, poster, frameCount = 45, maxFrameWidth = 640 }: ScrollVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const offscreenVideoRef = useRef<HTMLVideoElement>(null)
  const posterRef = useRef<HTMLImageElement>(null)
  const framesRef = useRef<ImageBitmap[]>([])
  const [framesReady, setFramesReady] = useState(false)
  const extractingRef = useRef(false)
  const rafRef = useRef<number>()
  const smoothedProgressRef = useRef(0)
  const targetProgressRef = useRef(0)
  const videoHasFrameRef = useRef(false)
  const durationRef = useRef(0)
  const [showPoster, setShowPoster] = useState(true)
  const [showVideo, setShowVideo] = useState(false)
  const [showCanvas, setShowCanvas] = useState(false)

  // Extract frames from offscreen video - non-blocking with yields
  const extractFrames = useCallback(async () => {
    if (extractingRef.current || !offscreenVideoRef.current) return
    extractingRef.current = true

    const offVideo = offscreenVideoRef.current
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('bitmaprenderer')!

    try {
      // Wait for metadata
      await new Promise<void>((resolve, reject) => {
        offVideo.onloadedmetadata = () => resolve()
        offVideo.onerror = () => reject(new Error('Failed to load video'))
        offVideo.currentTime = 0.01
      })

      const duration = offVideo.duration || 10
      durationRef.current = duration
      const count = Math.min(frameCount, Math.max(15, Math.floor(duration * 8)))
      framesRef.current = []

      // Extract frames in small batches with yields
      for (let i = 0; i < count; i++) {
        const t = (i / (count - 1)) * Math.max(0, duration - 0.05)
        offVideo.currentTime = t
        await new Promise<void>((resolve, reject) => {
          const onSeeked = () => {
            offVideo.removeEventListener('seeked', onSeeked)
            resolve()
          }
          offVideo.addEventListener('seeked', onSeeked, { once: true })
          setTimeout(() => reject(new Error('Seek timeout')), 1500)
        })

        const w = Math.min(offVideo.videoWidth, maxFrameWidth)
        const h = Math.round((w / offVideo.videoWidth) * offVideo.videoHeight)
        canvas.width = w
        canvas.height = h
        ctx.transferFromImageBitmap(await createImageBitmap(offVideo))
        const bitmap = await createImageBitmap(canvas)
        framesRef.current.push(bitmap)

        // Yield to main thread every 5 frames
        if (i % 5 === 0) {
          await new Promise(r => setTimeout(r, 0))
        }
      }

      setFramesReady(true)
      setShowCanvas(true)
      setShowVideo(false)
    } catch (e) {
      console.warn('Frame extraction failed, using seek fallback:', e)
      setFramesReady(false)
    } finally {
      extractingRef.current = false
    }
  }, [frameCount, maxFrameWidth])

  // Scroll progress calculation
  const updateProgress = useCallback(() => {
    const scrollY = window.scrollY
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    targetProgressRef.current = Math.max(0, Math.min(1, scrollY / scrollHeight))
  }, [])

  // Main animation loop
  useEffect(() => {
    let lastFrameTime = 0

    const animate = (now: number) => {
      // Throttle to ~60fps
      if (now - lastFrameTime < 16) {
        rafRef.current = requestAnimationFrame(animate)
        return
      }
      lastFrameTime = now

      // Smooth progress with lerp
      smoothedProgressRef.current += (targetProgressRef.current - smoothedProgressRef.current) * 0.12
      const p = smoothedProgressRef.current

      if (framesReady && framesRef.current.length > 0 && canvasRef.current) {
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')!
        const dpr = Math.min(window.devicePixelRatio, 2)
        const frameIndex = Math.floor(p * (framesRef.current.length - 1))
        const frame = framesRef.current[frameIndex]

        if (canvas.width !== canvas.clientWidth * dpr || canvas.height !== canvas.clientHeight * dpr) {
          canvas.width = canvas.clientWidth * dpr
          canvas.height = canvas.clientHeight * dpr
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        // object-cover draw
        const scale = Math.max(canvas.width / frame.width, canvas.height / frame.height)
        const dw = frame.width * scale
        const dh = frame.height * scale
        const dx = (canvas.width - dw) / 2
        const dy = (canvas.height - dh) / 2
        ctx.drawImage(frame as unknown as CanvasImageSource, dx, dy, dw, dh)
      } else if (videoRef.current && videoHasFrameRef.current && durationRef.current) {
        // Use requestVideoFrameCallback for smoother seeking if available
        const videoEl = videoRef.current
        const targetTime = p * Math.max(0, durationRef.current - 0.05)
        const currentTime = videoEl.currentTime
        
        if (Math.abs(currentTime - targetTime) > 0.03) {
          // Use native video frame callback for smooth seeking
          if ('requestVideoFrameCallback' in videoEl) {
            ;(videoEl as HTMLVideoElement).requestVideoFrameCallback(() => {
              ;(videoEl as HTMLVideoElement).currentTime = targetTime
            })
          } else {
            ;(videoEl as HTMLVideoElement).currentTime = targetTime
          }
        }
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    updateProgress()
    rafRef.current = requestAnimationFrame(animate)

    const onScroll = () => updateProgress()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [updateProgress])

  // Video event handlers
  const handleVideoLoadedData = useCallback(() => {
    videoHasFrameRef.current = true
    setShowVideo(true)
    setShowPoster(false)
    // Start frame extraction after a small delay
    setTimeout(() => extractFrames(), 500)
  }, [extractFrames])

  const handleVideoError = useCallback(() => {
    console.error('Video failed to load')
    setShowPoster(false)
    setShowVideo(false)
  }, [])

  const handlePosterLoad = useCallback(() => {
    setShowPoster(true)
  }, [])

  return (
    <div
      className="fixed inset-0 z-0 overflow-hidden pointer-events-none"
      style={{ background: '#0a0a0a' }}
      aria-hidden="true"
    >
      {/* Poster - shows first, fades out when video has frame */}
      {poster && (
        <img
          ref={posterRef}
          src={poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
          style={{ opacity: showPoster ? 1 : 0 }}
          onLoad={handlePosterLoad}
        />
      )}

      {/* Video element - visible while frames extracting, then fades out */}
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
        style={{
          opacity: showVideo && !framesReady ? 1 : 0,
          visibility: showVideo && !framesReady ? 'visible' : 'hidden',
        }}
        onLoadedData={handleVideoLoadedData}
        onError={handleVideoError}
      />

      {/* Canvas - draws scrubbed frames when ready */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
        style={{
          opacity: showCanvas ? 1 : 0,
          visibility: showCanvas ? 'visible' : 'hidden',
        }}
        aria-hidden="true"
      />

      {/* Offscreen video for frame extraction */}
      <video
        ref={offscreenVideoRef}
        src={src}
        muted
        playsInline
        preload="auto"
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </div>
  )
}